import { Link, useLocation } from 'react-router-dom'
import { useI18n } from '@/lib/i18n'
import type { EventConfig } from '@/types'
import { isInherited, type SettingsValues } from '../../../server/lib/eventSettings'
import InheritedSettings from './InheritedSettings'
import PosesPage from '../pages/PosesPage'
import ScreenEditor from '../pages/ScreenEditor'
import PrintLayoutEditor from '../pages/PrintLayoutEditor'
import BrandingSettings from '../pages/BrandingSettings'
import SharingSettings from '../pages/SharingSettings'
import GallerySettings from '../pages/GallerySettings'
import KioskSettings from '../pages/KioskSettings'
import EffectsSettings from '../pages/EffectsSettings'
import SurveyBuilder from '../pages/SurveyBuilder'
import VirtualAttendantSettings from '../pages/VirtualAttendantSettings'
import TriggersSettings from '../pages/TriggersSettings'
import RemoteControl from '../pages/RemoteControl'
import { GROUP_SECTIONS, type EventGroup } from '@/lib/settingsPresentation'

export default function EventSettingsGroup({ group, event, storedEvent, defaults, onChange }: {
  group: EventGroup; event: EventConfig; storedEvent: EventConfig; defaults: SettingsValues; onChange: (updates: Partial<EventConfig>) => void
}) {
  const { t } = useI18n()
  const location = useLocation()
  const sections = GROUP_SECTIONS[group] || []
  const inherited = sections.filter(key => isInherited(storedEvent, key)).length
  const props = { event, onChange }
  const tabs = group === 'capture' ? ['settings', 'poses'] : group === 'design' ? ['screens', 'print', 'branding'] : group === 'guest' ? ['sharing', 'gallery', 'slideshow-access'] : group === 'experience' ? ['effects', 'survey'] : group === 'advanced' ? ['triggers', 'remote'] : []
  const segment = location.pathname.split('/').filter(Boolean)[4]
  const activeTab = tabs.includes(segment) || (group === 'experience' && segment === 'attendant') ? segment : tabs[0]
  const showInherited = !tabs.length || activeTab === 'settings'
  return <div className="space-y-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-xl font-bold">{t(`inheritance.nav.${group}`)}</h1>
      {sections.length > 0 && <span className="text-xs text-muted-foreground">{t('inheritance.counts', { inherited, custom: sections.length - inherited })}</span>}
    </div>
    {tabs.length > 0 && <nav aria-label={t(`inheritance.nav.${group}`)} className="flex flex-wrap gap-2 border-b border-border pb-3">
      {tabs.map(tab => <Link key={tab} to={`/admin/events/${event.id}/${group}/${tab}`} aria-current={activeTab === tab ? 'page' : undefined} className={`rounded-lg px-3 py-2 text-sm ${activeTab === tab ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted'}`}>{t(`inheritance.tabs.${tab}`)}</Link>)}
    </nav>}
    {showInherited && sections.map(section => <InheritedSettings key={`${event.id}-${section}`} section={section} event={event} storedEvent={storedEvent} defaults={defaults} onChange={onChange} />)}
    {group === 'display' && <Link className="inline-block text-sm text-primary hover:underline" to={`/admin/events/${event.id}/guest/slideshow-access`}>{t('inheritance.manageAccess')}</Link>}
    {activeTab === 'poses' && <PosesPage {...props} />}
    {activeTab === 'screens' && <ScreenEditor {...props} />}
    {activeTab === 'print' && <PrintLayoutEditor {...props} />}
    {activeTab === 'branding' && <BrandingSettings {...props} />}
    {activeTab === 'sharing' && <SharingSettings {...props} />}
    {activeTab === 'gallery' && <GallerySettings {...props} />}
    {activeTab === 'slideshow-access' && <KioskSettings {...props} scope="access" />}
    {activeTab === 'effects' && <EffectsSettings {...props} />}
    {activeTab === 'survey' && <SurveyBuilder {...props} />}
    {activeTab === 'attendant' && <VirtualAttendantSettings {...props} />}
    {activeTab === 'triggers' && <TriggersSettings {...props} />}
    {activeTab === 'remote' && <RemoteControl />}
  </div>
}
